import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import axios from "axios";
import { API_URL, OFFERS } from "@/lib/checkout";
import { LogoMark } from "@/components/Logo";

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState("checking");
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("invalid");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "failed" || data.status === "expired") {
          setState("failed");
          return;
        }
      } catch (e) {
        // keep polling
      }
      tries.current += 1;
      if (tries.current > 12) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2500);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const lookup = sessionStorage.getItem("aih_offer") || "aih_full_assessment";
  const isProduct = lookup.startsWith("aih_prod_");
  const offer = OFFERS[lookup] || { name: isProduct ? "Your digital product" : "Business Growth Assessment", price: isProduct ? "" : OFFERS.aih_full_assessment.price };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-5 py-24 grain">
      <div className="max-w-lg w-full text-center rounded-3xl border border-gold/25 bg-obsidian-800/70 p-10">
        <div className="flex justify-center mb-6">
          <LogoMark className="w-14 h-14" />
        </div>

        {state === "checking" && (
          <div data-testid="payment-status-checking">
            <Loader2 className="w-10 h-10 text-gold animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold text-white mt-6">Confirming payment…</h1>
            <p className="text-slate-400 text-sm mt-3">This only takes a moment.</p>
          </div>
        )}

        {state === "paid" && (
          <div data-testid="payment-status-paid">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h1 className="font-display text-3xl font-bold text-white mt-6">Payment received</h1>
            <p className="text-slate-400 mt-3">
              Welcome aboard — your <span className="text-gold font-semibold">{offer.name}</span>
              {offer.price ? ` (${offer.price})` : ""} is confirmed.
            </p>
            <div className="mt-8 text-left space-y-3">
              {(isProduct
                ? ["Your payment is confirmed", "Your product is prepared for you — usually seconds", "Open it, print it, and put it to work"]
                : ["Complete the business intake form", "Book your one-on-one strategy session", "Receive your assessment and recommended next steps"]
              ).map((s, i) => (
                <div key={s} className="flex gap-3 items-center text-sm text-slate-300">
                  <span className="font-mono text-[10px] text-gold w-5 h-5 rounded-full border border-gold/40 flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  {s}
                </div>
              ))}
            </div>
            {isProduct ? (
              <Link
                data-testid="success-cta-product"
                to={`/product-view?session=${encodeURIComponent(sessionId)}`}
                className="block mt-9 w-full rounded-full bg-gold text-obsidian font-display font-bold py-4 hover:bg-gold-bright transition-colors"
              >
                Open Your Product
              </Link>
            ) : (
              <Link
                data-testid="success-cta-intake"
                to={`/intake?session=${encodeURIComponent(sessionId)}&type=${lookup === "aih_express_snapshot" ? "express" : "full"}`}
                className="block mt-9 w-full rounded-full bg-gold text-obsidian font-display font-bold py-4 hover:bg-gold-bright transition-colors"
              >
                Complete Your Intake Form
              </Link>
            )}
          </div>
        )}

        {(state === "failed" || state === "timeout" || state === "invalid") && (
          <div data-testid="payment-status-error">
            <XCircle className="w-12 h-12 text-red-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-white mt-6">
              {state === "invalid" ? "Missing payment reference" : "We couldn't confirm your payment yet"}
            </h1>
            <p className="text-slate-400 text-sm mt-3">
              If you completed checkout, keep your receipt and contact us — we'll sort it out. You can
              also try again from the offer page.
            </p>
            <Link
              data-testid="payment-error-home-link"
              to="/"
              className="inline-block mt-8 rounded-full border border-gold/60 text-gold font-display font-bold px-7 py-3.5 hover:bg-gold hover:text-obsidian transition-colors"
            >
              Back to Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
